import React from 'react';

type Enrolled = { cards: string; additionalCards: string } | string;

type PricingCardProps = {
  title: string;
  setupFee: number | string;
  previousSetupFee?: number | string;
  monthlyFee: number | string;
  forUpEnrolledCards?: string | null;
  features: {
    domain: string;
    referralLinkAlias?: string;
    countriesLanguages: string;
    SingleSignOnIntegration: boolean;
    CustomAPIsAndIntegrations: boolean;
    enrolled: Enrolled;
    support: string;
    cashbackFrequency?: string;
    standoutFeatures?: string | JSX.Element;
    appMode: string;
  };
};

function formatMoney(v: number | string) {
  if (typeof v === 'number') return `$${v.toLocaleString()}`;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : String(v);
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  setupFee,
  previousSetupFee,
  monthlyFee,
  forUpEnrolledCards,
  features,
}) => {
  return (
    <div className=' px-6 pt-8 pb-16 bg-white sm:rounded-lg overflow-hidden text-left'>
      {/* Title as BADGE */}
      <div className='mb-2'>
        <span className='inline-flex items-center rounded-full bg-[#0B51C5] text-white px-4 py-1 text-xl font-extrabold'>
          {title}
        </span>
      </div>

      {/* Setup fee row */}
      <div className='flex items-baseline gap-3 mt-3'>
        {previousSetupFee && (
          <p className='text-2xl font-semibold text-gray-500 line-through'>
            {formatMoney(previousSetupFee)}
          </p>
        )}
        <p className='text-2xl font-extrabold'>{formatMoney(setupFee)}</p>
      </div>
      <p className='text-gray-500 font-semibold mt-1'>One-time setup fee</p>

      {/* Monthly fee */}
      <p className='text-2xl font-extrabold mt-6'>{formatMoney(monthlyFee)}</p>
      <p className='text-gray-500 font-semibold text-sm mt-1'>
        Monthly service fee
      </p>

      {/* For up to X enrolled cards */}
      {forUpEnrolledCards && (
        <p className='text-gray-500 font-semibold text-sm mt-2'>
          For up to {forUpEnrolledCards} enrolled cards
        </p>
      )}

      {/* Separator full width */}

      {/* Includes section */}
      <div className='mt-6 sm:border sm:rounded-lg sm:p-5'>
        <h4 className='text-lg font-extrabold text-[#00C389] sm:pb-5'>
          {title}
        </h4>
        <div className='w-full border-t border-gray-200 '></div>

        <ul className='mt-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-6'>
          <li>
            <p className='font-bold text-sm'>Monthly Card Fee:</p>
            {typeof features.enrolled === 'object' ? (
              <p className='text-gray-500 font-semibold text-sm'>
                Up to {features.enrolled.cards} cards, additional{' '}
                {features.enrolled.additionalCards} cards +$250/m
              </p>
            ) : (
              <p className='text-gray-500 font-semibold text-sm'>
                {features.enrolled}
              </p>
            )}
          </li>
          <li>
            <p className='font-bold text-sm'>App Mode:</p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.appMode}
            </p>
          </li>
          <li>
            <p className='font-bold text-sm'>Countries & Languages:</p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.countriesLanguages &&
              features.countriesLanguages.includes('\n')
                ? features.countriesLanguages.split('\n').map((line, index) => (
                    <span key={index}>
                      {line}
                      {index <
                        features.countriesLanguages.split('\n').length - 1 && (
                        <br />
                      )}
                    </span>
                  ))
                : features.countriesLanguages}
            </p>
          </li>
          <li>
            <p className='font-bold text-sm'>Partner support:</p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.support}
            </p>
          </li>
          <li>
            <p className='font-bold text-sm'>Domain:</p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.domain}
            </p>
          </li>

          {features.referralLinkAlias && (
            <li>
              <p className='font-bold text-sm'>Referral Link Alias:</p>
              <p className='text-gray-500 font-semibold text-sm'>
                {features.referralLinkAlias}
              </p>
            </li>
          )}

          <li>
            <p className='font-bold text-sm'>Single Sign-On Integration:</p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.SingleSignOnIntegration ? 'Yes' : 'No'}
            </p>
          </li>

          <li>
            <p className='font-bold text-sm text-nowrap'>
              Custom APIs and Integrations:
            </p>
            <p className='text-gray-500 font-semibold text-sm'>
              {features.CustomAPIsAndIntegrations ? 'Yes' : 'No'}
            </p>
          </li>

          {features.cashbackFrequency && (
            <li>
              <p className='font-bold text-sm'>Cashback payout frequency:</p>
              <p className='text-gray-500 font-semibold text-sm'>
                {features.cashbackFrequency}
              </p>
            </li>
          )}
        </ul>

        {features.standoutFeatures && (
          <>
            <p className='mt-6 text-[#00C389] font-semibold text-base'>
              Standout features:
            </p>
            <p className='text-gray-900 font-bold text-sm mt-3'>
              {features.standoutFeatures}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PricingCard;
